/**
 * Printable product photo uploader (single-product-printable.twig).
 *
 * Photos are staged on the server the moment they are picked, so the purchase
 * can never fail for a missing upload. Each slot maps to one magnet in the
 * selected pack; a slot is filled by an AJAX upload to the WordPress endpoint
 * (snapmagnetique_upload_photo), which validates and stores the file and returns
 * the session staging token. The token is written into the form's hidden field
 * so checkout can promote the files to the order. Add-to-cart stays disabled
 * until every slot is filled.
 *
 * Config arrives via wp_localize_script as window.SnapmagnetiqueUpload. The
 * configurator (product.js) owns pack selection and emits `sm:packchange`; this
 * module owns the slot grid, the counter, and the add-to-cart enablement.
 *
 * When the product has a print geometry (config.print), each slot also carries
 * how the customer framed the photo. print-preview.js owns the preview that
 * lets them move and zoom it and talks back through sm:cropchange /
 * sm:preview-replace / sm:preview-remove; this module remains the only writer
 * of slot state.
 */

import { ensureNonces, refreshNonces } from './nonces.js';
import { cropRect, isLowRes } from './print-preview.js';

const PLACEHOLDER =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CDBFB6" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';

/** Minimal sprintf for the %1$d / %2$d / %d / %s patterns used in our strings. */
function format(template, ...args) {
  let i = 0;
  return template
    .replace(/%(\d+)\$[ds]/g, (_, n) => String(args[Number(n) - 1] ?? ''))
    .replace(/%[ds]/g, () => String(args[i++] ?? ''));
}

/**
 * Boot the slot-grid uploader: reads window.SnapmagnetiqueUpload, wires the
 * dropzone / file input / slot grid, and keeps add-to-cart disabled until every
 * slot for the selected pack is filled. No-op if the config or product root is
 * absent, so it is safe to call on any page.
 */
export function initPhotoUpload() {
  const root = document.querySelector('[data-product-config]');
  const config = window.SnapmagnetiqueUpload;
  if (!root || !config) {
    return;
  }

  const form = root.querySelector('[data-upload-form]');
  const fileInput = root.querySelector('[data-upload-input]');
  const dropzone = root.querySelector('[data-upload-dropzone]');
  const slotGrid = root.querySelector('[data-slot-grid]');
  const counter = root.querySelector('[data-upload-counter]');
  const errorEl = root.querySelector('[data-upload-error]');
  const addBtn = root.querySelector('[data-add-to-cart]');
  const tokenInput = root.querySelector('[data-upload-token]');
  if (!form || !fileInput || !dropzone || !slotGrid || !addBtn) {
    return;
  }

  const i18n = config.i18n || {};
  // Null when the product has no print size. Everything preview-related below
  // is gated on it, so an unconfigured product behaves exactly as before.
  const print = config.print || null;
  const cropsInput = root.querySelector('[data-upload-crops]');
  const previewBtn = root.querySelector('[data-preview-open]');
  const selectedCard = root.querySelector('[data-pack-card][data-selected]') || root.querySelector('[data-pack-card]');
  let required = parseInt(selectedCard ? selectedCard.dataset.count : '0', 10) || 0;
  let priceLabel = (selectedCard && selectedCard.dataset.price) || (addBtn.dataset.priceLabel || '');
  let pendingStart = 0;

  // One entry per magnet:
  //   status  'empty' | 'uploading' | 'filled' | 'error'
  //   url     blob URL of the picked file, also the preview's image source
  //   fx, fy  position in percent of the available travel; 50/50 is centred
  //   zoom    1 = the photo fills the piece exactly, higher crops in further
  //   w, h    natural pixel size, filled in once the thumbnail decodes
  let slots = [];
  const emptySlot = () => ({ status: 'empty', url: '', fx: 50, fy: 50, zoom: 1, w: 0, h: 0 });

  // Rendered tiles, parallel to `slots`. Reused across repaints so a full-size
  // photo is not re-decoded on every state change, and so a focused tile is not
  // yanked out from under the customer. See render().
  let nodes = [];

  // In-flight first-upload promise while no staging token exists yet; shared
  // across assignFiles() batches so rapid successive picks/drops can never
  // mint two competing tokens (which would fragment the set server-side).
  let tokenMint = null;

  const productId = (form.querySelector('[name="product_id"]') || {}).value || '';
  const token = () => (tokenInput && tokenInput.value) || '';
  const setToken = (value) => {
    if (tokenInput && value) {
      tokenInput.value = value;
    }
  };

  /** Show an inline error, or hide the banner when called with a falsy message. */
  const showError = (message) => {
    if (!errorEl) {
      return;
    }
    if (message) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    } else {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
  };

  /** Number of slots holding a staged, server-confirmed photo. */
  const filledCount = () => slots.filter((s) => s.status === 'filled').length;
  /** True while any upload is still in flight (add-to-cart must stay disabled). */
  const isBusy = () => slots.some((s) => s.status === 'uploading');
  /**
   * True while the AJAX add-to-cart request is committing this staging token
   * (add-to-cart.js sets the marker). Every mutation of the set - uploads,
   * removals, pack resizes - must pause: deleting or adding staged files at
   * that moment desyncs the set the server is attaching to the cart line, and
   * a repaint would clobber the button's "Adding…" disabled state.
   */
  const addInFlight = () => addBtn.dataset.loading !== undefined;

  /**
   * Add or drop the "this will print soft" marker on a tile, in place.
   *
   * Kept out of the tile signature so a photo whose dimensions arrive after it
   * has been painted does not force the whole tile to be rebuilt.
   */
  const applyLowResBadge = (el, slot) => {
    const existing = el.querySelector('[data-slot-lowres]');
    const low = print && slot.status === 'filled' && isLowRes(print, slot.w, slot.h, slot.zoom);

    if (!low) {
      if (existing) {
        existing.remove();
      }
      return;
    }
    if (existing) {
      return;
    }

    const badge = document.createElement('span');
    badge.dataset.slotLowres = '';
    badge.title = i18n.lowResBadge || 'Low resolution';
    badge.className =
      'pointer-events-none absolute bottom-1 left-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-status-warn-ink text-[9px] font-bold leading-none text-white';
    badge.textContent = '!';
    el.appendChild(badge);
  };

  /**
   * Build one slot tile reflecting its status (empty / uploading / filled /
   * error): preview image, spinner, remove and preview buttons as appropriate.
   *
   * With a print geometry the tile takes the product's real proportions and the
   * photo is cropped the way it will actually be printed, so the grid is a row
   * of small true previews rather than a row of squares.
   */
  const renderSlot = (slot, index) => {
    const el = document.createElement('div');
    el.dataset.slot = '';
    el.dataset.index = String(index);
    el.dataset.status = slot.status;
    el.className =
      'group relative flex items-center justify-center overflow-hidden rounded-[8px] border bg-surface-warm ' +
      (print ? '' : 'aspect-square ') +
      (slot.status === 'error'
        ? 'border-solid border-[#B3261E]'
        : slot.status === 'filled'
          ? 'border-solid border-[#D8CFC2]' + (print ? ' cursor-pointer' : '')
          : 'border-dashed border-[#D8CFC2] cursor-pointer');
    if (print) {
      el.style.aspectRatio = `${print.width_mm}/${print.height_mm}`;
    }

    if (slot.url && (slot.status === 'filled' || slot.status === 'uploading' || slot.status === 'error')) {
      const img = document.createElement('img');
      img.src = slot.url;
      img.alt = '';
      img.decoding = 'async';
      // The tile renders the same slice the preview does, from the same
      // function, in percentages of the tile. So the grid can never disagree
      // with the preview, and neither needs measuring.
      if (print && slot.w > 0 && slot.h > 0) {
        const rect = cropRect(print, slot.w, slot.h, slot.zoom, slot.fx, slot.fy);
        img.style.position = 'absolute';
        // See print-preview.js: WooCommerce's unlayered img { max-width: 100% }
        // beats the utility class and would squash the photo.
        img.style.maxWidth = 'none';
        img.style.maxHeight = 'none';
        img.style.width = `${100 / rect.w}%`;
        img.style.height = `${100 / rect.h}%`;
        img.style.left = `${(-rect.x * 100) / rect.w}%`;
        img.style.top = `${(-rect.y * 100) / rect.h}%`;
      } else if (print) {
        // Dimensions not known yet: fill the tile until the decode lands.
        img.style.aspectRatio = `${print.width_mm}/${print.height_mm}`;
      }
      img.className =
        'block max-w-none ' +
        (print && slot.w > 0 ? '' : 'w-full object-cover ') +
        (print ? '' : 'aspect-square ') +
        (slot.status === 'uploading' ? 'opacity-50' : '');
      // The browser reports these post-EXIF-rotation, which is what the
      // customer sees and therefore what the resolution warning must judge.
      img.addEventListener(
        'load',
        () => {
          if (slots[index] !== slot) {
            return; // The set changed while this thumbnail was decoding.
          }
          const first = !(slot.w > 0);
          slot.w = img.naturalWidth;
          slot.h = img.naturalHeight;
          applyLowResBadge(el, slot);
          // The tile was painted on the no-dimensions fallback path. Repaint so
          // it renders through the same crop arithmetic as everything else,
          // rather than leaving two rendering paths live at once. Guarded on
          // `first` so this cannot loop.
          if (first) {
            render();
          }
        },
        { once: true }
      );
      el.appendChild(img);
    } else {
      el.innerHTML = PLACEHOLDER;
    }

    if (slot.status === 'uploading') {
      const spin = document.createElement('span');
      spin.className =
        'absolute h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent';
      el.appendChild(spin);
    }

    if (slot.status === 'filled') {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.slotRemove = '';
      remove.setAttribute('aria-label', i18n.remove || 'Remove photo');
      remove.className =
        'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white leading-none';
      remove.innerHTML =
        '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
      el.appendChild(remove);

      if (print) {
        // Tapping the tile opens the preview too; this button is what gives
        // keyboard and screen-reader users the same door, and what hints at it
        // on a pointer device. The tile itself stays a plain div so there is no
        // interactive element nested inside another.
        const preview = document.createElement('button');
        preview.type = 'button';
        preview.dataset.slotPreview = '';
        preview.setAttribute('aria-label', format(i18n.previewOne || 'Preview photo %d', index + 1));
        preview.className =
          'absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white leading-none opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white';
        preview.innerHTML =
          '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>';
        el.appendChild(preview);
      }

      applyLowResBadge(el, slot);
    }

    return el;
  };

  /**
   * Publish where the customer positioned each photo, as a JSON map of slot
   * index to an [x, y] percentage. Only photos moved off centre are listed, so
   * the common case posts nothing at all.
   *
   * The field lives inside the add-to-cart form, so it rides both the AJAX
   * submit (which builds its body with new FormData(form)) and the no-JS
   * fallback, and lands in $_POST for CartOrderBridge::add_cart_item_data().
   */
  const syncCrops = () => {
    if (!cropsInput || !print) {
      return;
    }
    const round = (n) => Math.round(n * 100000) / 100000;
    const map = {};
    slots.forEach((slot, index) => {
      if (slot.status !== 'filled' || !(slot.w > 0) || !(slot.h > 0)) {
        return;
      }
      const untouched =
        Math.abs(slot.fx - 50) < 0.5 && Math.abs(slot.fy - 50) < 0.5 && Math.abs(slot.zoom - 1) < 0.001;
      if (untouched) {
        return; // The default crop; the server computes it, we say nothing.
      }
      const rect = cropRect(print, slot.w, slot.h, slot.zoom, slot.fx, slot.fy);
      map[index] = [round(rect.x), round(rect.y), round(rect.w), round(rect.h)];
    });
    cropsInput.value = Object.keys(map).length ? JSON.stringify(map) : '';
  };

  /** What a tile's appearance depends on. Unchanged signature means reuse it. */
  const slotSignature = (slot) => `${slot.status}|${slot.url}|${slot.fx}|${slot.fy}|${slot.zoom}|${slot.w}`;

  /** Repaint the grid, the counter, and the add-to-cart enabled state. */
  const render = () => {
    syncCrops();

    // Reconcile rather than rebuild. slotGrid.innerHTML = '' would re-decode
    // every full-size photo on each keystroke of state (a 46-pack is a lot of
    // bitmap), and would detach the tile the customer is standing on, which
    // silently breaks returning focus from the preview modal.
    const desired = slots.map((slot, index) => {
      const signature = slotSignature(slot);
      const cached = nodes[index];
      if (cached && cached.dataset.sig === signature) {
        return cached;
      }
      const el = renderSlot(slot, index);
      el.dataset.sig = signature;
      nodes[index] = el;
      return el;
    });
    nodes.length = desired.length;

    desired.forEach((node, index) => {
      const currentNode = slotGrid.children[index];
      if (currentNode === node) {
        return;
      }
      if (currentNode) {
        slotGrid.replaceChild(node, currentNode);
      } else {
        slotGrid.appendChild(node);
      }
    });
    while (slotGrid.children.length > desired.length) {
      slotGrid.lastElementChild.remove();
    }

    if (previewBtn) {
      const anyFilled = filledCount() > 0;
      previewBtn.classList.toggle('hidden', !anyFilled);
      previewBtn.classList.toggle('inline-flex', anyFilled);
    }

    // Tell an open preview what the set looks like now. It is inline and not
    // modal on desktop, so photos can be added, removed or dropped by a pack
    // change while the customer is looking at one. print-preview.js ignores
    // this unless membership actually changed.
    if (print) {
      root.dispatchEvent(new CustomEvent('sm:photos', { detail: { photos: photoList() } }));
    }

    if (counter) {
      counter.textContent = format(i18n.counter || '%1$d of %2$d added', filledCount(), required);
    }
    // While the AJAX add is committing the set, the button belongs to
    // add-to-cart.js ("Adding…", disabled) - never repaint it out of that state.
    if (addBtn && !addInFlight()) {
      addBtn.disabled = !(filledCount() === required && required > 0 && !isBusy());
      if (priceLabel) {
        addBtn.textContent = format(i18n.addToCart || 'Add to cart · %s', priceLabel);
      }
    }
  };

  /**
   * Grow or shrink the slot set to `count` magnets after a pack change. Trailing
   * slots that are dropped have their object URLs revoked and any staged file
   * removed from the server.
   */
  const resize = (count) => {
    if (count === slots.length) {
      return;
    }
    if (count < slots.length) {
      // Drop the trailing slots, removing any staged files for them.
      for (let i = count; i < slots.length; i++) {
        if (slots[i].url) {
          URL.revokeObjectURL(slots[i].url);
        }
        if (slots[i].status === 'filled') {
          removeFromServer(i);
        }
      }
      slots = slots.slice(0, count);
    } else {
      for (let i = slots.length; i < count; i++) {
        slots.push(emptySlot());
      }
    }
    required = count;
    render();
  };

  // -- Client-side guards (mirrors the server validator's first checks) ------
  const clientReject = (file) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const okType =
      (config.allowedExt || []).includes(ext) &&
      (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === '');
    if (!okType) {
      return i18n.badType || 'Only JPG and PNG photos are allowed.';
    }
    if (config.maxBytes && file.size > config.maxBytes) {
      return i18n.tooLarge || 'That photo is too large.';
    }
    return null;
  };

  /**
   * Pick up to `n` empty/error slot indices to fill, starting at `start` and
   * wrapping to the front so a mid-grid pick still uses every open slot.
   */
  const emptyIndicesFrom = (start, n) => {
    const out = [];
    for (let i = Math.max(0, start); i < slots.length && out.length < n; i++) {
      if (slots[i].status === 'empty' || slots[i].status === 'error') {
        out.push(i);
      }
    }
    // Wrap to the front if the user started mid-grid.
    for (let i = 0; i < start && out.length < n; i++) {
      if (slots[i].status === 'empty' || slots[i].status === 'error') {
        out.push(i);
      }
    }
    return out;
  };

  /**
   * Distribute a picked/dropped FileList across open slots from `start`. Extra
   * files beyond the open count are ignored and surfaced in the counter message.
   */
  const assignFiles = async (fileList, start) => {
    if (addInFlight()) {
      return;
    }
    showError('');
    const files = Array.from(fileList);
    if (!files.length) {
      return;
    }
    const targets = emptyIndicesFrom(start, files.length);
    const picked = files.slice(0, targets.length);

    // Establish a single staging token before fanning out: a multi-file batch
    // would otherwise fire parallel requests with an empty token and mint one
    // token per file, fragmenting the set. Upload one at a time until a token
    // exists (covers a failed first file), then parallelise the rest. The mint
    // promise is shared across batches, so a second pick/drop landing before
    // the first response waits for that token instead of minting its own.
    let k = 0;
    while (!token() && k < picked.length) {
      if (tokenMint) {
        await tokenMint; // Another batch is minting; wait, then re-check.
        continue;
      }
      tokenMint = uploadToSlot(targets[k], picked[k]).finally(() => {
        tokenMint = null;
      });
      await tokenMint;
      k++;
    }
    for (; k < picked.length; k++) {
      uploadToSlot(targets[k], picked[k]);
    }

    if (files.length > targets.length) {
      showError(format(i18n.counter || '%1$d of %2$d added', filledCount(), required));
    }
  };

  /**
   * Stage one file into `index`: optimistic preview, AJAX upload, then mark the
   * slot filled (or error). Persists the returned staging token. Returns a
   * Promise that always resolves so callers can await sequencing.
   */
  const uploadToSlot = (index, file) => {
    const rejection = clientReject(file);
    if (rejection) {
      showError(rejection);
      return Promise.resolve();
    }

    const slot = slots[index];
    if (!slot) {
      // The pack shrank while this batch was queued; the target slot is gone.
      return Promise.resolve();
    }
    if (slot.url) {
      URL.revokeObjectURL(slot.url);
    }
    slot.url = URL.createObjectURL(file);
    slot.status = 'uploading';
    // A new file means a new photo: drop the previous framing and pixel size
    // rather than carrying them onto something they never described.
    slot.fx = 50;
    slot.fy = 50;
    slot.zoom = 1;
    slot.w = 0;
    slot.h = 0;
    render();

    // The slot object is replaced when the set is reset or the pack shrinks, so
    // a captured-reference mismatch means this response is stale.
    const stale = () => slots[index] !== slot;

    // Rebuilt per attempt: a FormData carrying a file cannot be posted twice.
    const send = () => {
      const body = new FormData();
      body.append('action', 'snapmagnetique_upload_photo');
      body.append('nonce', config.nonce);
      body.append('token', token());
      body.append('index', String(index));
      if (productId) {
        body.append('product_id', productId);
      }
      body.append('photo', file);

      return fetch(config.ajaxUrl, { method: 'POST', credentials: 'same-origin', body }).then((r) => r.json());
    };

    // The nonce printed into this page may have expired in the full-page cache,
    // so take a live one before spending the customer's bandwidth on the file.
    return ensureNonces()
      .then(send)
      .then((res) => {
        // Nonces also turn over every 12 hours, which can land mid-session:
        // fetch a replacement and resend instead of surfacing an error.
        if (res && !res.success && res.data && res.data.code === 'bad_nonce') {
          return refreshNonces().then(send);
        }
        return res;
      })
      .then((res) => {
        if (res && res.success && res.data) {
          setToken(res.data.token);
          if (stale()) {
            // The set changed under us; drop the now-orphaned staged file.
            removeServerSlot(res.data.token, index);
          } else {
            slots[index].status = 'filled';
          }
          return;
        }
        const code = res && res.data && res.data.code;
        const message = (res && res.data && res.data.message) || i18n.failed || 'Upload failed.';
        if (code === 'stale_token') {
          resetAll();
        } else if (!stale()) {
          slots[index].status = 'error';
        }
        showError(message);
      })
      .catch(() => {
        if (!stale()) {
          slots[index].status = 'error';
          showError(i18n.failed || 'Upload failed.');
        }
      })
      .finally(() => {
        if (!stale()) {
          render();
        }
      });
  };

  /** Fire-and-forget request to drop the staged file at `index` for token `tok`. */
  const removeServerSlot = (tok, index) => {
    if (!tok) {
      return;
    }
    ensureNonces().then(() => {
      const body = new FormData();
      body.append('action', 'snapmagnetique_remove_photo');
      body.append('nonce', config.nonce);
      body.append('token', tok);
      body.append('index', String(index));
      return fetch(config.ajaxUrl, { method: 'POST', credentials: 'same-origin', body });
    }).catch(() => {});
  };

  /** Drop the staged file at `index` using the current form token. */
  const removeFromServer = (index) => removeServerSlot(token(), index);

  // The server rejected the token (the session rotated). Drop everything and
  // start a fresh set; the next upload mints a new token.
  const resetAll = () => {
    if (tokenInput) {
      tokenInput.value = '';
    }
    slots.forEach((s) => {
      if (s.url) {
        URL.revokeObjectURL(s.url);
      }
    });
    slots = Array.from({ length: required }, emptySlot);
    render();
  };

  /** Reset a single slot to empty, removing its staged file and revoking its URL. */
  const clearSlot = (index) => {
    if (addInFlight()) {
      return; // The set is being committed to the cart - don't mutate it.
    }
    const slot = slots[index];
    if (!slot) {
      return;
    }
    if (slot.status === 'filled') {
      removeFromServer(index);
    }
    if (slot.url) {
      URL.revokeObjectURL(slot.url);
    }
    slots[index] = emptySlot();
    showError('');
    render();
  };

  // -- Events --------------------------------------------------------------
  /** Open the native file dialog, remembering `start` so the change handler fills from there. */
  const openPicker = (start) => {
    if (addInFlight()) {
      return;
    }
    pendingStart = start;
    fileInput.value = '';
    fileInput.click();
  };

  dropzone.addEventListener('click', () => openPicker(0));
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker(0);
    }
  });

  ['dragenter', 'dragover'].forEach((type) => {
    dropzone.addEventListener(type, (e) => {
      e.preventDefault();
      dropzone.setAttribute('data-dragover', '');
    });
  });
  ['dragleave', 'drop'].forEach((type) => {
    dropzone.addEventListener(type, (e) => {
      e.preventDefault();
      dropzone.removeAttribute('data-dragover');
    });
  });
  dropzone.addEventListener('drop', (e) => {
    if (e.dataTransfer && e.dataTransfer.files) {
      assignFiles(e.dataTransfer.files, 0);
    }
  });

  fileInput.addEventListener('change', () => {
    if (fileInput.files && fileInput.files.length) {
      assignFiles(fileInput.files, pendingStart);
    }
  });

  slotGrid.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-slot-remove]');
    if (removeBtn) {
      e.stopPropagation();
      clearSlot(parseInt(removeBtn.closest('[data-slot]').dataset.index, 10));
      return;
    }
    const previewToggle = e.target.closest('[data-slot-preview]');
    if (previewToggle) {
      e.stopPropagation();
      openPreview(parseInt(previewToggle.closest('[data-slot]').dataset.index, 10));
      return;
    }
    const slotEl = e.target.closest('[data-slot]');
    if (!slotEl) {
      return;
    }
    if (slotEl.dataset.status === 'empty' || slotEl.dataset.status === 'error') {
      openPicker(parseInt(slotEl.dataset.index, 10));
    } else if (slotEl.dataset.status === 'filled') {
      // Tapping a filled tile did nothing before this feature. It is the
      // natural gesture on a phone, where the hover-revealed eye never appears.
      openPreview(parseInt(slotEl.dataset.index, 10));
    }
  });

  // -- Print preview ---------------------------------------------------------
  // print-preview.js owns the modal; this module stays the only writer of slot
  // state, so everything it wants to change comes back as an event.

  /** The staged set as the modal needs it: filled photos, in grid order. */
  const photoList = () =>
    slots
      .map((slot, index) => ({ index, status: slot.status, url: slot.url, fx: slot.fx, fy: slot.fy, zoom: slot.zoom, w: slot.w, h: slot.h }))
      .filter((photo) => photo.status === 'filled' && photo.url);

  /**
   * Open the preview at `index`, or at the first filled photo when that slot
   * holds nothing. Suppressed while the set is being committed to the cart, for
   * the same reason every other mutation is.
   */
  const openPreview = (index) => {
    if (!print || addInFlight()) {
      return;
    }
    const list = photoList();
    if (!list.length) {
      return;
    }
    const target = list.some((photo) => photo.index === index) ? index : list[0].index;
    root.dispatchEvent(new CustomEvent('sm:preview', { detail: { index: target, photos: list } }));
  };

  // The "Preview" button in the upload header has no slot of its own, so it
  // asks for the set and we answer with the first filled photo.
  root.addEventListener('sm:preview-request', () => openPreview(-1));

  root.addEventListener('sm:cropchange', (e) => {
    const detail = e.detail || {};
    const slot = slots[detail.index];
    if (!slot || !Number.isFinite(detail.x) || !Number.isFinite(detail.y) || !Number.isFinite(detail.zoom)) {
      return;
    }
    slot.fx = Math.max(0, Math.min(100, detail.x));
    slot.fy = Math.max(0, Math.min(100, detail.y));
    slot.zoom = Math.max(1, detail.zoom);
    render(); // repaints the tile and republishes the crops field
  });

  root.addEventListener('sm:preview-replace', (e) => {
    const index = (e.detail || {}).index;
    if (typeof index !== 'number' || !slots[index]) {
      return;
    }
    // Free the slot first so the picked file lands back in it rather than in
    // the next empty one: emptyIndicesFrom() only ever targets open slots.
    clearSlot(index);
    openPicker(index);
  });

  root.addEventListener('sm:preview-remove', (e) => {
    const index = (e.detail || {}).index;
    if (typeof index === 'number') {
      clearSlot(index);
    }
  });

  root.addEventListener('sm:packchange', (e) => {
    if (addInFlight()) {
      return; // product.js blocks the click too; belt and braces.
    }
    if (e.detail && e.detail.price) {
      priceLabel = e.detail.price;
    }
    resize(parseInt(e.detail ? e.detail.count : required, 10) || required);
  });

  // Block submit if somehow incomplete (defence in depth behind the disabled btn).
  form.addEventListener('submit', (e) => {
    if (filledCount() !== required || required < 1 || isBusy()) {
      e.preventDefault();
      showError(i18n.incomplete || 'Please add a photo for every magnet before adding to cart.');
    }
  });

  // After a successful AJAX add (assets/js/add-to-cart.js) the staging token is
  // committed to a cart line, so clear the grid + token for a fresh set; the
  // next upload mints a new token. Without this the same files could be re-added.
  form.addEventListener('sm:reset-after-add', () => {
    resetAll(); // clears the token, revokes URLs, re-empties the slots and repaints
    showError('');
  });

  // Initial paint.
  slots = Array.from({ length: required }, emptySlot);
  render();
}
